import { get, patch } from './apiClient';

export interface ClientPriceListItem {
  itemCode?: string;
  description: string;
  packaging?: string;
  unit_price: number;
  vat: boolean;
  markup?: number | null;
  base_price?: number | null;
  category?: string;
  sub_category?: string;
  source?: string;
  last_updated?: string | null;
  keywords?: string[];
  quantity?: number;
  remarks?: string;
  internal_remarks?: string;
  // Section A
  client_remarks?: string;
  section?: 'provision' | 'cabin' | 'additional';
  uom?: string;
}

export interface ClientPriceListTotals {
  perSection: {
    provision: { qty: number; selling: number };
    cabin: { qty: number; selling: number };
    additional: { qty: number; selling: number };
  };
  grand: { qty: number; selling: number };
}

export interface ClientPriceList {
  _id: string;
  name: string;
  items: ClientPriceListItem[];
  createdAt?: string;
  updatedAt?: string;
  sentAt?: string | null;
  // Section A
  responseStatus?: 'draft' | 'sent' | 'responded' | 'confirmed';
  respondedAt?: string | null;
  confirmedOrderId?: string | null;
  clientName?: string;
  vesselName?: string;
  totals?: ClientPriceListTotals;
  /** The secret in the client's link (D116.1). Only the staff `/details` read carries it, never a list row. */
  accessToken?: string;
}

export interface ClientPriceListListResponse {
  success: boolean;
  data: ClientPriceList[];
  page: number;
  limit: number;
  total: number;
}

export interface ClientPriceListDetailResponse {
  success: boolean;
  data: ClientPriceList;
}

export async function getClientPriceLists(params?: {
  page?: number;
  limit?: number;
}): Promise<ClientPriceListListResponse> {
  const qs = new URLSearchParams();
  if (params?.page != null) qs.set('page', String(params.page));
  if (params?.limit != null) qs.set('limit', String(params.limit));
  const query = qs.toString();
  return get<ClientPriceListListResponse>(
    `/client-price-lists${query ? `?${query}` : ''}`
  );
}

/**
 * What the CLIENT sees of a price list — the public link's answer
 * (backend `utils/clientPriceListClientView.js`, D112.2). No cost, markup,
 * internal remarks, supplier or totals: the public page works out its own.
 * A missing number reads null.
 */
export interface ClientPriceListClientItem {
  description: string;
  uom: string;
  packaging: string;
  category: string;
  sub_category: string;
  section: string;
  remarks: string;
  client_remarks: string;
  unit_price: number | null;
  quantity: number | null;
}

export interface ClientPriceListClientView {
  _id: string;
  name: string;
  clientName: string;
  vesselName: string;
  items: ClientPriceListClientItem[];
}

export interface ClientPriceListClientViewResponse {
  success: boolean;
  data: ClientPriceListClientView;
}

/**
 * PUBLIC (no account): the client's view of a list, with the secret from the
 * link's `?t=` (D116.1; a wrong or missing one is 404 CLIENT_PRICE_LIST_LINK_INVALID).
 * Staff screens use getClientPriceListDetails.
 */
export async function getClientPriceListForClient(
  id: string,
  token: string
): Promise<ClientPriceListClientViewResponse> {
  return get<ClientPriceListClientViewResponse>(`/client-price-lists/${id}?t=${encodeURIComponent(token)}`);
}

/** STAFF (authenticated, Quotations view): the full list with cost, markup, internal remarks and totals. */
export async function getClientPriceListDetails(
  id: string
): Promise<ClientPriceListDetailResponse> {
  return get<ClientPriceListDetailResponse>(`/client-price-lists/${id}/details`);
}

export async function updateClientPriceList(
  id: string,
  payload: { items: Array<{ markup?: number; quantity?: number; remarks?: string; internal_remarks?: string }> }
): Promise<ClientPriceListDetailResponse> {
  return patch<ClientPriceListDetailResponse>(`/client-price-lists/${id}`, payload);
}

export interface SendPriceListPayload {
  recipients: string;
  subject: string;
  body: string;
  /** Only when the person saw the already-sent warning (D180). */
  resend?: true;
}

export async function sendClientPriceList(
  id: string,
  payload: SendPriceListPayload
): Promise<{ success: boolean; message: string }> {
  const { postJson } = await import('./apiClient');
  return postJson<{ success: boolean; message: string }>(`/client-price-lists/${id}/send`, payload);
}

/** Copy Client Link flow — mark the list "Sent" without emailing (see markSent on the backend). */
export async function markClientPriceListSent(id: string): Promise<ClientPriceListDetailResponse> {
  const { postJson } = await import('./apiClient');
  return postJson<ClientPriceListDetailResponse>(`/client-price-lists/${id}/mark-sent`, {});
}

export async function deleteClientPriceList(id: string): Promise<{ success: boolean; message: string }> {
  const { deleteRequest } = await import('./apiClient');
  return deleteRequest<{ success: boolean; message: string }>(`/client-price-lists/${id}`);
}

// Section A (A2) — client posts back filled quantities
export async function respondToClientPriceList(
  id: string,
  token: string,
  payload: { items: Array<{ quantity?: number; client_remarks?: string }> }
): Promise<ClientPriceListClientViewResponse> {
  return patch<ClientPriceListClientViewResponse>(`/client-price-lists/${id}/respond?t=${encodeURIComponent(token)}`, payload);
}

// Section A (A6) — convert responded list to ConfirmedOrder
export async function convertPriceListToConfirmedOrder(
  id: string
): Promise<{ success: boolean; data: { confirmedOrderId: string; confirmedOrder: any } }> {
  const { postJson } = await import('./apiClient');
  return postJson(`/client-price-lists/${id}/convert-to-confirmed-order`, {});
}

/** Trigger download of price list Excel export. */
export async function downloadClientPriceListExport(id: string, listName: string): Promise<void> {
  const { API_BASE_URL } = await import('./apiClient');
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE_URL}/client-price-lists/${id}/export`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error('Export failed');
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `PriceList_${(listName || 'Export').replace(/[^a-zA-Z0-9_-]/g, '_')}_${Date.now()}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
